import { LLMConfigService, LLMConfig } from './LLMConfigService';
import { ApiTokensService } from '@/hooks/api-tokens/service';
import { OpenAIWrapper } from '@/services/openai/OpenAIWrapper';
import { GeminiWrapper } from '@/services/gemini/GeminiWrapper';
import { LovableAIWrapper } from '@/services/lovableai/LovableAIWrapper';

export interface UnifiedLLMParams {
  brandId: string;
  source: string;
  messages?: Array<{ 
    role: string; 
    content: string | Array<{ type: string; text?: string; image_url?: { url: string } }> 
  }>;
  prompt?: string; // For Gemini-style calls
  temperature?: number;
  max_tokens?: number; // For OpenAI/LovableAI
  maxOutputTokens?: number; // For Gemini
  stream?: boolean;
  // Override config if needed
  provider?: 'openai' | 'gemini' | 'lovable' | 'claude' | 'perplexity';
  model?: string;
}

export interface UnifiedLLMResponse {
  content: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  finish_reason?: string;
  model?: string;
  provider?: string;
}

export class UnifiedLLMService {
  private static configCache: Map<string, { config: LLMConfig | null; timestamp: number }> = new Map();
  private static readonly CACHE_TTL = 60000; // 1 minute cache

  /**
   * Main method to call LLM with automatic provider/model selection based on configuration
   */
  static async callLLM(params: UnifiedLLMParams): Promise<UnifiedLLMResponse> {
    const { brandId, source, provider: overrideProvider, model: overrideModel, ...restParams } = params;

    console.log(`[UnifiedLLMService] callLLM called for source: "${source}", brandId: "${brandId}", overrideProvider: "${overrideProvider}"`);

    // Get configuration for this source
    let config: LLMConfig | null = null;
    
    if (!overrideProvider && !overrideModel) {
      config = await this.getCachedConfig(brandId, source);
      console.log(`[UnifiedLLMService] Config lookup result:`, config ? {
        provider: config.provider,
        model: config.model,
        isActive: config.isActive
      } : 'No config found');
    }

    // Determine provider and model
    const provider = overrideProvider || config?.provider || 'openai';
    const model = overrideModel || config?.model || await this.getDefaultModel(brandId, provider);

    console.log(`[UnifiedLLMService] Using provider: "${provider}", model: "${model}"`);

    // Get API tokens
    const apiTokens = await ApiTokensService.fetchTokens(brandId);
    const apiKey = this.getApiKey(apiTokens, provider);

    if (!apiKey) {
      throw new Error(`${provider} API key not configured for this brand`);
    }

    // Merge metadata from config with params
    const temperature = restParams.temperature ?? config?.metadata?.temperature;
    const max_tokens = restParams.max_tokens ?? config?.metadata?.max_tokens;
    const maxOutputTokens = restParams.maxOutputTokens ?? config?.metadata?.maxOutputTokens;

    // Route to appropriate wrapper
    switch (provider) {
      case 'openai':
        return this.callOpenAI({
          apiKey,
          model,
          source,
          brandId,
          messages: restParams.messages || this.promptToMessages(restParams.prompt),
          temperature,
          max_tokens,
          stream: restParams.stream
        });

      case 'gemini':
        return this.callGemini({
          apiKey,
          model,
          source,
          brandId,
          prompt: restParams.prompt || this.messagesToPrompt(restParams.messages),
          temperature,
          maxOutputTokens
        });

      case 'lovable':
        return this.callLovableAI({
          apiKey,
          model,
          source,
          brandId,
          messages: this.normalizeMessages(restParams.messages || this.promptToMessages(restParams.prompt)),
          temperature,
          max_tokens,
          stream: restParams.stream,
          skipConfigCheck: true
        });

      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  /**
   * Call OpenAI wrapper
   */
  private static async callOpenAI(params: {
    apiKey: string;
    model: string;
    source: string;
    brandId: string;
    messages: Array<{ role: string; content: string | Array<{ type: string; text?: string; image_url?: { url: string } }> }>;
    temperature?: number;
    max_tokens?: number;
    stream?: boolean;
  }): Promise<UnifiedLLMResponse> {
    const wrapper = new OpenAIWrapper();
    // Skip config check since we already determined the provider/model from config
    const response = await wrapper.chatCompletion({ ...params, skipConfigCheck: true });

    return {
      content: response.choices[0]?.message?.content || '',
      usage: response.usage,
      finish_reason: response.choices[0]?.finish_reason,
      model: params.model,
      provider: 'openai'
    };
  }

  /**
   * Call Gemini wrapper
   */
  private static async callGemini(params: {
    apiKey: string;
    model: string;
    source: string;
    brandId: string;
    prompt: string;
    temperature?: number;
    maxOutputTokens?: number;
  }): Promise<UnifiedLLMResponse> {
    const wrapper = new GeminiWrapper();
    // Skip config check since we already determined the provider/model from config
    const response = await wrapper.generateContent({ ...params, skipConfigCheck: true });

    return {
      content: response.candidates[0]?.content?.parts[0]?.text || '',
      usage: response.usageMetadata ? {
        prompt_tokens: response.usageMetadata.promptTokenCount || 0,
        completion_tokens: response.usageMetadata.candidatesTokenCount || 0,
        total_tokens: response.usageMetadata.totalTokenCount || 0
      } : undefined,
      model: params.model,
      provider: 'gemini'
    };
  }

  /**
   * Call LovableAI wrapper
   */
  private static async callLovableAI(params: {
    apiKey: string;
    model: string;
    source: string;
    brandId?: string;
    messages: Array<{ role: string; content: string }>;
    temperature?: number;
    max_tokens?: number;
    stream?: boolean;
    skipConfigCheck?: boolean;
  }): Promise<UnifiedLLMResponse> {
    const wrapper = new LovableAIWrapper();
    // Skip config check since we already determined the provider/model from config
    const response = await wrapper.chatCompletion({ ...params, skipConfigCheck: true });

    return {
      content: response.choices[0]?.message?.content || '',
      usage: response.usage,
      finish_reason: response.choices[0]?.finish_reason,
      model: params.model,
      provider: 'lovable'
    };
  }

  /**
   * Get cached config or fetch from database
   */
  private static async getCachedConfig(brandId: string, source: string): Promise<LLMConfig | null> {
    const cacheKey = `${brandId}:${source}`;
    const cached = this.configCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.config;
    }

    const config = await LLMConfigService.getLLMConfig(brandId, source);
    this.configCache.set(cacheKey, { config, timestamp: Date.now() });
    return config;
  }

  /**
   * Get default model for provider from api_tokens
   */
  private static async getDefaultModel(brandId: string, provider: string): Promise<string> {
    const apiTokens = await ApiTokensService.fetchTokens(brandId);

    switch (provider) {
      case 'openai':
        return apiTokens.openai_model || 'gpt-4o-mini';
      case 'gemini':
        return apiTokens.gemini_model || 'gemini-2.5-flash-lite';
      case 'lovable':
        return 'google/gemini-2.5-flash'; // Default Lovable model
      case 'claude':
        return apiTokens.claude_model || 'claude-3-5-haiku-20241022';
      case 'perplexity':
        return apiTokens.perplexity_model || 'sonar';
      default:
        return 'gpt-4o-mini';
    }
  }

  /**
   * Get API key for provider from api_tokens
   */
  private static getApiKey(apiTokens: any, provider: string): string | null {
    switch (provider) {
      case 'openai':
        return apiTokens.openai || null;
      case 'gemini':
        return apiTokens.gemini || null;
      case 'lovable':
        // Lovable uses a special API key
        // Note: Lovable API key may need to be added to api_tokens table
        // For now, check if there's a lovable key, otherwise throw error
        // In production, you may want to add 'lovable' to api_tokens table
        if ((apiTokens as any).lovable) {
          return (apiTokens as any).lovable;
        }
        throw new Error('Lovable API key not configured. Please add it to API tokens settings.');
      case 'claude':
        return apiTokens.claude || null;
      case 'perplexity':
        return apiTokens.perplexity || null;
      default:
        return null;
    }
  }

  /**
   * Normalize messages to simple string content format
   */
  private static normalizeMessages(messages: Array<{ role: string; content: string | Array<{ type: string; text?: string; image_url?: { url: string } }> }>): Array<{ role: string; content: string }> {
    return messages.map(msg => ({
      role: msg.role,
      content: typeof msg.content === 'string' 
        ? msg.content 
        : msg.content
            .filter(part => part.type === 'text' && part.text)
            .map(part => part.text)
            .join(' ')
    }));
  }

  /**
   * Convert prompt string to messages array
   */
  private static promptToMessages(prompt?: string): Array<{ role: string; content: string }> {
    if (!prompt) {
      return [{ role: 'user', content: '' }];
    }
    return [{ role: 'user', content: prompt }];
  }

  /**
   * Convert messages array to prompt string (for Gemini)
   */
  private static messagesToPrompt(messages?: Array<{ role: string; content: string | Array<{ type: string; text?: string; image_url?: { url: string } }> }>): string {
    if (!messages || messages.length === 0) {
      return '';
    }

    // Combine all messages into a single prompt
    return messages.map(msg => {
      if (typeof msg.content === 'string') {
        return `${msg.role}: ${msg.content}`;
      } else {
        // Handle array content (e.g., multimodal)
        const textParts = msg.content
          .filter(part => part.type === 'text' && part.text)
          .map(part => part.text)
          .join(' ');
        return `${msg.role}: ${textParts}`;
      }
    }).join('\n\n');
  }

  /**
   * Clear config cache (useful after updates)
   */
  static clearCache(brandId?: string, source?: string): void {
    if (brandId && source) {
      this.configCache.delete(`${brandId}:${source}`);
    } else if (brandId) {
      // Clear all configs for this brand
      const keysToDelete: string[] = [];
      this.configCache.forEach((_, key) => {
        if (key.startsWith(`${brandId}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.configCache.delete(key));
    } else {
      // Clear all
      this.configCache.clear();
    }
  }
}

